// Creative Strategist project list (Task #2375).
//
// Every project for the brand, active first. The home screen only shows the
// active ones plus a short "recently completed" tail; this is the full list.

import { useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Compass, Plus } from "lucide-react";
import { CS_TERMINAL_STATUSES, type CreativeStrategistProject } from "@/lib/creative-strategist";
import { ProjectCard } from "@/pages/creative-strategist";

export default function CreativeStrategistProjectsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();

  const { data: projects, isLoading } = useQuery<CreativeStrategistProject[]>({
    queryKey: [`/api/creative-strategist/brands/${brandId}/projects`],
    enabled: !!brandId,
  });

  const active = (projects ?? []).filter((p) => !CS_TERMINAL_STATUSES.has(p.status));
  const finished = (projects ?? []).filter((p) => CS_TERMINAL_STATUSES.has(p.status));

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Compass className="h-4 w-4" />
            Creative Strategist
          </div>
          <h1 className="text-2xl font-bold tracking-tight mt-1" data-testid="text-projects-title">
            Projects
          </h1>
        </div>
        <Button
          className="rounded-full px-5"
          onClick={() => navigate(`/brands/${brandId}/creative-strategist`)}
          data-testid="button-new-project"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New project
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      ) : (projects ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No projects yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {active.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold tracking-tight">Active</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {active.map((p) => (
                  <ProjectCard key={p.id} project={p} brandId={brandId!} />
                ))}
              </div>
            </section>
          )}
          {finished.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold tracking-tight">Closed</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {finished.map((p) => (
                  <ProjectCard key={p.id} project={p} brandId={brandId!} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
